import clsx from 'clsx';
import {
  type FocusEvent,
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { XmarkIcon } from '@dynatrace/strato-icons';

import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useId } from '../../core/hooks/useId.js';
import { TimeErrorKey } from '../../core/time/shared.types.js';
import { useDateTimeRangePickerStore } from '../../core/time/timeframeStore/useTimeframeStore.js';
import {
  formatTimeValidationMessage,
  getValidityStateKeyForTimeError,
  validate,
} from '../../core/time/validation.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { fieldTokens } from '../../styles/field.sty.js';
import { textStyle } from '../../styles/textStyle.sty.js';
import {
  clearIconButton,
  dateTimePicker,
} from '../date-time-picker/DateTimePicker.sty.js';
import { focusDateTimeInput } from '../date-time-picker/utils/focusDateTimeInput.js';
import type { ValidateFieldOptions } from '../form/form.types.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import { FormFieldMessageContext } from '../forms-core/contexts/FormFieldMessageContext.js';
import type { UseFormControlReturnType } from '../forms-core/hooks/useFormControl.js';
import { getCustomMessageIds } from '../forms-core/state/formfield-messages-selectors.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useContainerFocus } from '../hooks/useContainerFocus.js';
import { SpinButtonHandler } from '../spin-buttons/spin-button-group/types.js';
import {
  type ValidationState,
  type ValidationTrigger,
} from '../validation/types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import { getValidationState } from '../validation/validation-builders.js';
import { DateRangeSelector } from './DateRangeSelector.js';
import { DateTimeInput } from './DateTimeInput.js';
import * as styles from './DateTimeRangePicker.sty.js';
import { isExpressionTriggerKey } from './expression-trigger-helpers.js';
import { useDateTimeRangePicker } from './hooks/useDateTimeRangePicker.js';
import { onTimeframeCopyCapture } from './selection-copy-helpers.js';
import {
  peekPreRestoreEndpoints,
  type SelectionEndpoints,
} from './selection-helpers.js';
import type {
  DateTimeRangePickerProps,
  DateTimeRangePickerRef,
} from './types.js';
import { DateRangeSelectorRef, DateTimeInputRef } from './types.js';
import { useSelectionInteractionHandlers } from './useSelectionInteractionHandlers.js';

/**
 * Returns true if a given DateTimeInput element contains the active (focused)
 * element *or* the window selection intersects it.
 *
 * The focus-based check covers the common case where a single spin button is
 * focused – including empty (placeholder) spin buttons whose `selectAll()` is
 * a no-op and therefore leave the window selection collapsed.
 *
 * The selection-based check covers cross-input scenarios such as Ctrl+A or
 * drag-select spanning both "from" and "to" inputs.
 */
function isInputInSelectionOrFocused(
  ref: RefObject<DateTimeInputRef | null>,
  preRestoreEndpoints?: SelectionEndpoints,
): boolean {
  const element = ref.current?.element;
  if (!element) {
    return false;
  }

  if (element.contains(document.activeElement)) {
    return true;
  }

  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
    if (selection.getRangeAt(0).intersectsNode(element)) {
      return true;
    }
  }

  // Safari clips the live selection to the active CE host after contenteditable
  // is re-attached in the keydown capture phase. Fall back to the pre-restore
  // endpoints stashed by deferContentEditableRestore, which still span both inputs.
  return (
    !!preRestoreEndpoints &&
    (element.contains(preRestoreEndpoints.anchorNode) ||
      element.contains(preRestoreEndpoints.focusNode))
  );
}

function focusExpressionInputAtEnd(
  ref: RefObject<DateTimeInputRef | null>,
): void {
  const expressionElement = ref.current?.expressionInputRef;
  if (!expressionElement) {
    return;
  }

  expressionElement.focus();

  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(expressionElement);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

function getOffsetWithinElement(
  element: HTMLElement,
  container: Node,
  offset: number,
): number {
  const range = document.createRange();
  range.selectNodeContents(element);
  range.setEnd(container, offset);
  return range.toString().length;
}

/**@internal */
export const DateTimeRangePickerRoot = forwardRef<
  DateTimeRangePickerRef | null,
  Omit<
    DateTimeRangePickerProps,
    | 'value'
    | 'defaultValue'
    | 'onChange'
    | 'disabled'
    | 'required'
    | 'id'
    | 'aria-disabled'
    | 'readOnly'
    | 'controlState'
  > & {
    formControl: UseFormControlReturnType;
    shouldSuppressBlur?: (relatedTarget: Element | null) => boolean;
  }
>((props, forwardedRef) => {
  const {
    name,
    _form,
    _messageTarget,
    style: consumerStyle,
    className: consumerClassName,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    placeholder: placeholderProp,
    onBlur,
    onFocus,
    formControl,
    onValidityChange,
    shouldSuppressBlur,
    ...remainingProps
  } = props;
  const {
    id,
    disabled,
    required,
    readOnly = false,
    ariaDisabled,
    inputProps,
    showError,
    messageState,
    messageDispatch: formMessageDispatch,
  } = formControl;
  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);
  const ariaProps = useAriaLabelingProps(remainingProps);
  const COMPONENT_NAME = 'DateTimeRangePicker';

  const rootRef = useRef<HTMLDivElement>(null);
  const touchedContainerRef = useRef<HTMLDivElement>(null);
  const dateTimeToRef = useRef<DateTimeInputRef>(null);
  const dateRangeSelectorRef = useRef<DateRangeSelectorRef>(null);

  const ownerId = useId();

  const [hasInputContent, setHasInputContent] = useState(false);

  const setValue = useDateTimeRangePickerStore(
    (state) => state.setDateTimeRangePickerTimeframe,
  );
  const value = useDateTimeRangePickerStore(
    (state) => state.dateTimeRangePickerTimeframe,
  );
  const dateTimeFromRef = useDateTimeRangePickerStore(
    (state) => state.dateTimeFromRef,
  );
  const precision = useDateTimeRangePickerStore((state) => state.precision);
  const min = useDateTimeRangePickerStore((state) => state.min);
  const max = useDateTimeRangePickerStore((state) => state.max);
  const formatString = useDateTimeRangePickerStore(
    (state) => state.formatString,
  );

  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const { placeholder, onPasteCapture, onUndoPasteKeyDown } =
    useDateTimeRangePicker({
      placeholder: placeholderProp,
      readOnly,
      dateTimeFromRef,
      dateTimeToRef,
    });

  const intl = useIntl();

  const customMessageIdsRef = useRef<string[]>([]);
  customMessageIdsRef.current = getCustomMessageIds(messageState);

  // Track the last error key returned by the imperative validate() so we can
  // pass it as `formerError` on the next call and clean up stale messages.
  const lastValidationErrorRef = useRef<TimeErrorKey | undefined>(undefined);
  const formValidityChangeRef = useRef<
    ((state: ValidationState) => void) | undefined
  >(undefined);
  const _emitValidationState = useValidationStateEmitter((state) =>
    formValidityChangeRef.current?.(state),
  );

  const buildValidationState = useCallback(
    (
      trigger: ValidationTrigger,
      key: TimeErrorKey | undefined,
    ): ValidationState => {
      if (key === undefined) {
        return { status: 'done', trigger, errors: [] };
      }
      return getValidationState(
        getValidityStateKeyForTimeError(key),
        trigger,
        formatTimeValidationMessage(key, intl, {
          value,
          min,
          max,
          precision,
          expectedFormat: formatString,
        }),
        id ? [id] : [],
      );
    },
    [intl, value, min, max, precision, formatString, id],
  );

  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () => buildValidationState('manual', getErrorKey()),
  );

  // Provide root and input element reference and functions to interact with the calendar overlay
  useImperativeHandle(
    forwardedRef,
    (): DateTimeRangePickerRef => ({
      element: rootRef.current,
      inputRef: {
        from: dateTimeFromRef.current?.inputRef,
        to: dateTimeToRef.current?.inputRef,
      },
      open: () => dateRangeSelectorRef.current?.open(),
      close: () => dateRangeSelectorRef.current?.close(),
      focus: () => dateTimeFromRef.current?.focus?.(),
      getCurrentValue: () => ({
        from: value?.from ?? null,
        to: value?.to ?? null,
      }),
      validate() {
        lastValidationErrorRef.current = validate(value, min, max, {
          formMessageDispatch,
          intl,
          formerError: lastValidationErrorRef.current,
          precision,
          showError: true,
          checkOrder: true,
          ref: rootRef,
          expectedFormat: formatString,
          valueMode: 'dateTimePicker',
          required,
        });
        formMessageDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        emitValidationState(
          buildValidationState('manual', lastValidationErrorRef.current),
        );
      },
      // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
      validateAsync: async () => {
        lastValidationErrorRef.current = validate(value, min, max, {
          formMessageDispatch,
          intl,
          formerError: lastValidationErrorRef.current,
          precision,
          showError: true,
          checkOrder: true,
          ref: rootRef,
          expectedFormat: formatString,
          valueMode: 'dateTimePicker',
          required,
        });
        formMessageDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        return emitValidationState(
          buildValidationState('manual', lastValidationErrorRef.current),
        );
      },
    }),
    [
      value,
      min,
      max,
      formMessageDispatch,
      intl,
      precision,
      formatString,
      required,
      dateTimeFromRef,
      emitValidationState,
      buildValidationState,
    ],
  );

  // Computes the current range error key without dispatching messages, used to
  // keep `onValidityChange` in sync on automatic (change / blur) updates.
  const getErrorKey = useCallback(
    () =>
      validate(value, min, max, {
        precision,
        checkOrder: true,
        ref: rootRef,
        expectedFormat: formatString,
        valueMode: 'dateTimePicker',
        required,
      }),
    [value, min, max, precision, formatString, required],
  );

  const { containerClassName, ...containerFocusProps } = useContainerFocus({
    onFocus,
    onBlur,
    disabled,
    overlayOwnerId: ownerId,
    emitValidationState,
    getBlurValidationState: () => buildValidationState('blur', getErrorKey()),
    shouldSuppressBlur,
  });

  const validateField = useCallback(
    async ({
      reveal,
      trigger,
    }: ValidateFieldOptions): Promise<ValidationState> => {
      if (reveal) {
        formMessageDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      }
      const errorKey = getErrorKey();
      return emitValidationState(buildValidationState(trigger, errorKey));
    },
    [
      formMessageDispatch,
      getErrorKey,
      emitValidationState,
      buildValidationState,
    ],
  );

  const resetValidation = useCallback(() => {
    formMessageDispatch?.({
      type: FormFieldMessagesAction.SETISTOUCHED,
      payload: { isTouched: false },
    });
    for (const id of customMessageIdsRef.current) {
      formMessageDispatch?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: { id },
      });
    }
  }, [formMessageDispatch]);

  const formRegistration = useInternalFormRegistration({
    id,
    name,
    form: _form,
    messageTarget: _messageTarget,
    onValidityChange,
    getValue: () => value,
    focus: () =>
      dateTimeFromRef.current?.inputRef?.focus({ focusVisible: true }),
    validateField,
    resetValidation,
  });
  formValidityChangeRef.current = formRegistration.onValidityChange;

  // Surface validation state on every value change (`change` trigger).
  useEffect(() => {
    emitValidationState(buildValidationState('change', getErrorKey()));
  }, [value, emitValidationState, buildValidationState, getErrorKey]);

  const { focusClassName } = useFocusRing({
    focusWithin: true,
    ignoreModality: true,
    isMinimal: false,
    disabled,
    color: showError ? 'critical' : 'neutral',
  });

  const dateTimeFromLabel = intl.formatMessage({
    defaultMessage: 'DateTime from',
    id: 'fMy6q/KKOqbvCA3H',
    description:
      'Textbox to enter a relative or absolute timeframe from value.',
  });

  const dateTimeToLabel = intl.formatMessage({
    defaultMessage: 'DateTime to',
    id: 'uuhhc3nEZptwtV2E',
    description: 'Textbox to enter a relative or absolute timeframe to value.',
  });
  const clearButtonLabel = intl.formatMessage({
    defaultMessage: 'Clear date and time range',
    id: 'zX4r/Y8gJL1C8+5e',
    description: 'Aria label for the DateTimeRangePicker clear button.',
  });
  const formFieldMessageContextValue = useMemo(
    () => ({
      state: {
        errors: [],
        hints: [],
        showError,
      },
    }),
    [showError],
  );

  const {
    onMouseDown,
    onKeyDown: handleSelectionKeyDown,
    onContextMenu,
    onCutCapture,
  } = useSelectionInteractionHandlers(
    touchedContainerRef,
    useCallback(() => {
      focusDateTimeInput(null, dateTimeFromRef, dateTimeToRef);
    }, [dateTimeFromRef]),
    dateTimeFromRef,
    dateTimeToRef,
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (disabled || readOnly) {
        handleSelectionKeyDown(event);
        return;
      }

      // Safari: Reads the stash set by deferContentEditableRestore
      const preRestoreEndpoints = peekPreRestoreEndpoints(
        event.currentTarget as HTMLElement,
      );

      const fromInputIsIso =
        dateTimeFromRef.current?.element?.getAttribute('data-valuetype') ===
        'iso8601';
      const toInputIsIso =
        dateTimeToRef.current?.element?.getAttribute('data-valuetype') ===
        'iso8601';

      const bothInSelection =
        isInputInSelectionOrFocused(dateTimeFromRef, preRestoreEndpoints) &&
        isInputInSelectionOrFocused(dateTimeToRef, preRestoreEndpoints);

      // When both inputs are selected, allow expression trigger keys to switch the entire range to expression mode.
      if (bothInSelection && isExpressionTriggerKey(event)) {
        setValue(null);
        if (dateTimeToRef.current?.expressionInputRef) {
          dateTimeToRef.current.expressionInputRef.textContent = '';
        }
      }

      const switchFromToExpression =
        isInputInSelectionOrFocused(dateTimeFromRef, preRestoreEndpoints) &&
        fromInputIsIso;
      const switchToToExpression =
        isInputInSelectionOrFocused(dateTimeToRef, preRestoreEndpoints) &&
        toInputIsIso;
      if (
        isExpressionTriggerKey(event) &&
        (switchFromToExpression || switchToToExpression)
      ) {
        const char = event.key;

        // The primary input receives the character and focus.
        // When both are selected, 'from' receives the character and focus.
        const primaryRef = switchFromToExpression
          ? dateTimeFromRef
          : dateTimeToRef;

        primaryRef.current?.setExpressionValue(char);

        requestAnimationFrame(() => {
          focusExpressionInputAtEnd(primaryRef);
        });

        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Handle regular character input when both inputs are selected
      if (
        isInputInSelectionOrFocused(dateTimeFromRef, preRestoreEndpoints) &&
        isInputInSelectionOrFocused(dateTimeToRef, preRestoreEndpoints) &&
        !fromInputIsIso &&
        !toInputIsIso &&
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        const char = event.key;
        const fromExpressionElement =
          dateTimeFromRef.current?.expressionInputRef;
        const toExpressionElement = dateTimeToRef.current?.expressionInputRef;
        const selection = window.getSelection();

        if (
          fromExpressionElement &&
          toExpressionElement &&
          selection &&
          selection.rangeCount > 0 &&
          !selection.isCollapsed
        ) {
          const range = selection.getRangeAt(0);
          const fromValue = fromExpressionElement.textContent ?? '';
          const toValue = toExpressionElement.textContent ?? '';

          const fromStart = fromExpressionElement.contains(range.startContainer)
            ? getOffsetWithinElement(
                fromExpressionElement,
                range.startContainer,
                range.startOffset,
              )
            : 0;
          const toEnd = toExpressionElement.contains(range.endContainer)
            ? getOffsetWithinElement(
                toExpressionElement,
                range.endContainer,
                range.endOffset,
              )
            : toValue.length;

          dateTimeFromRef.current?.setExpressionValue(
            `${fromValue.slice(0, fromStart)}${char}`,
          );
          dateTimeToRef.current?.setExpressionValue(toValue.slice(toEnd));

          requestAnimationFrame(() => {
            focusExpressionInputAtEnd(dateTimeFromRef);
          });

          event.preventDefault();
          event.stopPropagation();
          return;
        }

        // Replace the selected expression in "from" and keep "to" unchanged.
        dateTimeFromRef.current?.setExpressionValue(char);

        requestAnimationFrame(() => {
          focusExpressionInputAtEnd(dateTimeFromRef);
        });

        event.preventDefault();
        event.stopPropagation();
        return;
      }

      handleSelectionKeyDown(event);
    },
    [
      disabled,
      readOnly,
      handleSelectionKeyDown,
      dateTimeFromRef,
      dateTimeToRef,
      setValue,
    ],
  );

  // Move from the last "from" segment to the first "to" segment.
  const handleFromTrailingEdge = useCallback(() => {
    dateTimeToRef.current?.focus?.();
  }, []);

  // Move from the first "to" segment to the last "from" segment.
  const handleToLeadingEdge = useCallback(() => {
    dateTimeFromRef.current?.focusLast();
  }, [dateTimeFromRef]);

  // Carry trailing overflow from "from" into the first "to" segment.
  const handleFromTrailingOverflow = useCallback(
    (overflowChar: string) => {
      const handlers = Array.from(
        dateTimeToRef.current?.spinButtonHandlers?.values() ?? [],
      ).filter((h): h is SpinButtonHandler => h !== null && h !== undefined);

      const firstHandler = handlers[0];
      if (!firstHandler) {
        return;
      }

      if (value?.to?.type === 'expression' || value?.to === null) {
        dateTimeToRef.current?.setExpressionValue(overflowChar);
        requestAnimationFrame(() => {
          const exprEl = dateTimeToRef.current?.expressionInputRef;
          if (exprEl) {
            // Focus first so that expressionOnFocusHandler fires and selects all,
            // then immediately collapse the selection to the end so the cursor
            // lands after the overflow character instead of selecting everything.
            exprEl.focus();
            const sel = window.getSelection();
            if (sel) {
              const range = document.createRange();
              range.selectNodeContents(exprEl);
              range.collapse(false);
              sel.removeAllRanges();
              sel.addRange(range);
            }
          }
        });
        return;
      }

      if (firstHandler.type === 'number') {
        firstHandler.applyText(overflowChar, true);
        firstHandler.element.current?.focus();
        firstHandler.setSelection({ start: 0, end: 0, rtl: true });
      }
    },
    [value?.to],
  );

  return (
    <Flex
      ref={rootRef}
      flexDirection="column"
      data-testid={dataTestId}
      style={consumerStyle}
      className={clsx(containerClassName, consumerClassName)}
      {...ariaProps}
      {...behavioralTrackingProps}
      data-dt-component={COMPONENT_NAME}
      {...containerFocusProps}
    >
      <FormFieldMessageContext.Provider value={formFieldMessageContextValue}>
        <InputGroup style={{ gridTemplateColumns: '1fr auto' }}>
          <Flex
            ref={touchedContainerRef}
            className={clsx(
              inputGroupClassName,
              dateTimePicker({
                readOnly,
                showError,
                disabled,
                type: 'datetime',
              }),
              textStyle({ textStyle: 'base' }),
              fieldTokens({
                color: showError ? 'critical' : 'neutral',
                disabled: disabledOrAriaDisabled,
                variant: 'emphasized',
              }),
              focusClassName,
              styles.dateTimeRange({
                showError,
                disabled: disabledOrAriaDisabled,
                readOnly: readOnly,
              }),
            )}
            onCopyCapture={onTimeframeCopyCapture}
            onCutCapture={onCutCapture}
            onPasteCapture={onPasteCapture}
            onInput={() => {
              setHasInputContent(
                !!dateTimeFromRef.current?.expressionInputRef?.textContent ||
                  !!dateTimeToRef.current?.expressionInputRef?.textContent,
              );
            }}
            tabIndex={disabled ? undefined : 0}
            onMouseDown={onMouseDown}
            onContextMenu={onContextMenu}
            onKeyDownCapture={(e) => {
              // Handles undo for from and to inputs with Ctrl+Z.
              // Uses capture phase so it fires before the input-level undo handler.
              onUndoPasteKeyDown(e);
            }}
            onKeyDown={onKeyDown}
            onFocus={(e: FocusEvent) => {
              // Forward the focus only if it is coming from outside this component.
              // Focus the first spin button with textContent; otherwise, focus the input.

              if (!rootRef.current?.contains(e.relatedTarget)) {
                focusDateTimeInput(e, dateTimeFromRef, dateTimeToRef);
              }
            }}
          >
            <span className={styles.scrollContainer}>
              <DateTimeInput
                ref={dateTimeFromRef}
                className={styles.dateTimeInput}
                disabled={disabled}
                readOnly={readOnly}
                placeholder={placeholder.from}
                name={name === undefined ? undefined : `${name}-from`}
                form={_form}
                // Maintains its own state to separate single and range value manipulations
                value={value === null ? null : value.from}
                onChange={(newValue) => {
                  if (typeof newValue !== 'string') {
                    setValue({ from: newValue ?? null });
                  }
                }}
                aria-label={dateTimeFromLabel}
                aria-disabled={ariaDisabled}
                required={required}
                controlId={id}
                inputProps={inputProps}
                onTrailingEdge={handleFromTrailingEdge}
                onTrailingOverflow={handleFromTrailingOverflow}
                partialTimeAnchorDate={value?.to?.absoluteDate || undefined}
                onPasteComplete={() => {
                  dateTimeToRef.current?.focus?.();
                }}
              />

              <span className={styles.timeframeSeparator}>→</span>
              <DateTimeInput
                ref={dateTimeToRef}
                className={styles.dateTimeInput}
                disabled={disabled}
                readOnly={readOnly}
                placeholder={placeholder.to}
                name={name === undefined ? undefined : `${name}-to`}
                form={_form}
                // Maintains its own state to separate single and range value manipulations
                value={value === null ? null : value.to}
                onChange={(newValue) => {
                  if (typeof newValue !== 'string') {
                    setValue({ to: newValue ?? null });
                  }
                }}
                aria-label={dateTimeToLabel}
                aria-disabled={ariaDisabled}
                required={required}
                onLeadingEdge={handleToLeadingEdge}
                dateOnlyPasteAnchor="endOfDay"
                partialTimeAnchorDate={value?.from?.absoluteDate || undefined}
                onPasteComplete={() => {
                  requestAnimationFrame(() => {
                    dateTimeToRef.current?.focusLast();
                  });
                }}
              />
            </span>
            {!readOnly &&
              !disabledOrAriaDisabled &&
              ((value &&
                (value.from?.value !== '' || value.to?.value !== '')) ||
                hasInputContent) && (
                <Button
                  onClick={() => {
                    // Focus before state updates so the button blurs with relatedTarget =
                    // expressionInputRef (inside boundary) rather than null (which would
                    // fire a spurious onBlur when the button unmounts after the clear).
                    dateTimeFromRef.current?.expressionInputRef?.focus();
                    setValue(null);
                    if (dateTimeFromRef.current?.expressionInputRef) {
                      dateTimeFromRef.current.expressionInputRef.textContent =
                        '';
                    }
                    if (dateTimeToRef.current?.expressionInputRef) {
                      dateTimeToRef.current.expressionInputRef.textContent = '';
                    }
                    setHasInputContent(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                    }
                  }}
                  className={clearIconButton}
                  color={showError ? 'critical' : 'neutral'}
                  size="condensed"
                  aria-label={clearButtonLabel}
                >
                  <XmarkIcon />
                </Button>
              )}
          </Flex>
          <DateRangeSelector
            ref={dateRangeSelectorRef}
            disabled={disabled}
            aria-disabled={ariaDisabled}
            readOnly={readOnly}
            error={showError}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
            overlayOwnerId={ownerId}
          />
        </InputGroup>
      </FormFieldMessageContext.Provider>
    </Flex>
  );
});
